import React, { useState } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { InvoiceDA } from '../../services/admin/invoice.service';
import { Error } from '../../models/error.model';
import { GlobalSwal } from '../../styles/swal.style';
import { Order } from '../../models/order.model';
import { Company } from '../../models/company.model';
import { modalHide } from '../../utils/modal.util';
import { getError } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';

import AppLoading from '../Loading';

export interface AppModalInvoiceDepositProps {
  companyForUser: Company | null,
  order: Order | null,
  onCreated: (invoiceId: string) => void
};

let errorResponse: Error;

const AppModalInvoiceDeposit: React.FunctionComponent<AppModalInvoiceDepositProps> = ({companyForUser, order, onCreated}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [amount, setAmount] = useState('')
  const [description, setDescription] = useState('')

  const orderTotal = order?.amount?.total ?? 0
  const invoicedAmount = order?.invoiced_amount ?? 0
  const remaining = Math.round((orderTotal - invoicedAmount) * 100) / 100

  const submitCreate = async () => {
    if (!companyForUser || !order) return

    const numAmount = Number(amount)
    if (!numAmount || numAmount <= 0) {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      })
      return
    }

    if (numAmount > remaining) {
      GlobalSwal.fire({
        text: lang.labels.depositAmountExceedsRemaining,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      })
      return
    }

    setLoadIndicator('on')

    await InvoiceDA.createPartialInvoice(companyForUser.id, order.id, {
      type: 'deposit',
      amount: numAmount,
      description: description || undefined
    }).then((response) => {
      if (response.status === 201) {
        const invoiceData = response.data

        GlobalSwal.fire({
          title: lang.labels.successfullyCreatedInvoice,
          text: lang.labels.actionCompletedRedirectingToPage,
          icon: 'success',
          showConfirmButton: false,
          timer: 2200
        }).then(() => {
          executeHideModal()
          onCreated(invoiceData.id)
        })
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch((error) => {
      errorWebHandler(lang, error)
    }).finally(() => {
      setLoadIndicator('off')
    })
  }

  const executeHideModal = () => {
    modalHide('modal-invoice-deposit')
    setTimeout(() => {
      setAmount('')
      setDescription('')
    }, 200)
  }

  return (
    <div id="modal-invoice-deposit" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-dialog-centered modal-md">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.depositInvoice}</h2>
          </div>
          <div className="modal-body">
            { order
              ?
              <div className="form">
                <div className="fv-row mb-5">
                  <div className="bg-light-primary rounded p-3 mb-3">
                    <span className="fw-bold">{lang.labels.total}: </span>
                    <span>{orderTotal.toFixed(2)} {order.currency?.symbol ?? order.currency?.code ?? ''}</span>
                    { invoicedAmount > 0 &&
                      <span className="ms-3 text-muted">
                        ({invoicedAmount.toFixed(2)} {lang.labels.invoicedAmountOf} {orderTotal.toFixed(2)})
                      </span>
                    }
                    <br/>
                    <span className="fw-bold">{lang.labels.remainingQuantity}: </span>
                    <span>{remaining.toFixed(2)} {order.currency?.symbol ?? order.currency?.code ?? ''}</span>
                  </div>
                </div>
                <div className="fv-row mb-5">
                  <label className="form-label required">{lang.labels.depositAmount}</label>
                  <input
                    className="form-control"
                    type="number"
                    step="0.01"
                    min={0}
                    max={remaining}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder={`Max: ${remaining.toFixed(2)}`}
                  />
                </div>
                <div className="fv-row mb-5">
                  <label className="form-label">{lang.labels.depositDescription}</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={lang.labels.depositInvoice}
                  />
                </div>
              </div>
              :
              <div className="text-center py-10">
                <span className="spinner-border text-primary"></span>
              </div>
            }
          </div>
          <div className="modal-footer">
            <button className="btn btn-light-primary" type="button" onClick={executeHideModal}>
              {lang.labels.cancel}
            </button>
            <button className="btn btn-primary" type="button" disabled={loadIndicator === 'on' || !amount || Number(amount) <= 0} onClick={submitCreate}>
              { loadIndicator === 'on'
                ? <AppLoading></AppLoading>
                : <span>{lang.labels.create}</span>
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppModalInvoiceDeposit;
